// Prototype "EqualEarth" -- carte du monde en projection Eckert IV (equal-area),
// remplacement possible de Leaflet/Mercator sur la page Carte (cf. resolution
// ONU du 5 septembre 2026, portee par le Togo/l'Union africaine, soutenue par
// la France). Page cachee (pas dans le menu, pas dans PAGE_PATHS), etape 3 du
// prototypage convenue avec Rudy : clustering + spiderfy, le morceau sans
// equivalent D3 tout fait (Leaflet.markercluster est une brique a part,
// aucune librairie D3 equivalente n'existe) -- reconstruit a la main :
//
// - Regroupement par PROXIMITE A L'ECRAN (pas juste coordonnees identiques,
//   deja gere par groupByLatLng plus bas) : algorithme glouton simple (pour
//   ~150 marqueurs, un index spatial serait premature) recalcule a chaque
//   changement de zoom, le seuil de distance (en unites "donnees", avant le
//   transform du groupe <g>) etant divise par l'echelle courante pour rester
//   equivalent a un rayon FIXE en pixels ecran quel que soit le zoom -- meme
//   principe que maxClusterRadius de Leaflet.markercluster.
// - Spiderfy : quand un cluster clique n'a presque aucune etendue geo (points
//   au meme endroit ou trop proches pour qu'un zoom les separe), ses membres
//   sont deployes en cercle autour du centre avec un trait fin ("patte
//   d'araignee", meme idee visuelle que Leaflet.markercluster) vers chacun.
// - Sinon, clic sur cluster = zoom anime vers l'emprise de ses membres.
//
// Bibliotheques : d3-geo (geoPath/geoGraticule) + d3-geo-projection (geoEckert4,
// absent de d3-geo core) + d3-selection + d3-zoom + topojson-client (pour lire
// le fichier de contours). Donnees : world-atlas/countries-50m.json, vendu en
// dur dans assets/data/ (copie par scripts/build.mjs, cf. copyVendorLibs()).
import { geoPath, geoGraticule, geoArea, geoCentroid } from 'd3-geo';
import { geoEckert4 } from 'd3-geo-projection';
import { select } from 'd3-selection';
import { zoom, zoomIdentity, zoomTransform } from 'd3-zoom';
import { feature } from 'topojson-client';
import { generateSVG } from '../../gen_svg.js';
import { resolveStaticSvg } from '../../utils/knockoutRecord.js';
import { escapeHtml } from '../../utils/stringUtils.js';
import { uploadMapPhoto, fetchAdminSession, logoutAdmin } from '../../services/apiClient.js';
import { createGeolocationService } from '../../services/geolocationService.js';
import { createMapPhotoUploadFeature } from '../map/mapPhotoUpload.js';
import { initHeaderMenu } from '../ui/headerMenu.js';
import { getCachedIsAdmin, setCachedIsAdmin } from '../../services/adminUiCache.js';

// Header du site (identique à sillage.html/ar.html) -- même mécanique de
// menu burger que les autres pages, cf. sillageMain.js. La page reste hors
// menu (aucun nav-link ne pointe vers /equalearth, aucun lien "active"
// dedans) : demande explicite de Rudy, seul le HABILLAGE change.
initHeaderMenu({
  headerEl: document.querySelector('header'),
  burgerBtn: document.getElementById('burger-btn'),
  navLinks: document.querySelectorAll('.header-nav .nav-link'),
});

(async () => {
  const dashboardLink = document.getElementById('dashboard-link');
  const adminLogoutBtn = document.getElementById('adminLogoutBtn');
  const cachedIsAdmin = getCachedIsAdmin();
  if (dashboardLink) dashboardLink.hidden = !cachedIsAdmin;
  if (adminLogoutBtn) adminLogoutBtn.hidden = !cachedIsAdmin;
  try {
    const data = await fetchAdminSession();
    const isAdmin = Boolean(data?.isAdmin);
    if (dashboardLink) dashboardLink.hidden = !isAdmin;
    if (adminLogoutBtn) adminLogoutBtn.hidden = !isAdmin;
    setCachedIsAdmin(isAdmin);
  } catch {
    if (dashboardLink) dashboardLink.hidden = true;
    if (adminLogoutBtn) adminLogoutBtn.hidden = true;
    setCachedIsAdmin(false);
  }
})();

document.getElementById('adminLogoutBtn')?.addEventListener('click', (e) => {
  e.target.disabled = true;
  logoutAdmin().catch(() => {}).finally(() => { window.location.href = '/'; });
});

const svg = select('#equalearth-svg');
const root = svg.append('g').attr('class', 'ew-root');
const landLayer = root.append('g').attr('class', 'ew-land');
const graticuleLayer = root.append('g').attr('class', 'ew-graticule');
// labelLayer AVANT markerLayer : les noms de pays/villes (demande Rudy)
// doivent rester SOUS les marqueurs/clusters, pas les recouvrir.
const labelLayer = root.append('g').attr('class', 'ew-labels');
const legsLayer = root.append('g').attr('class', 'ew-legs');
const markerLayer = root.append('g').attr('class', 'ew-markers');

const popupEl = document.getElementById('equalearth-popup');
const popupCloseBtn = document.getElementById('equalearth-popup-close');

const projection = geoEckert4();
const path = geoPath(projection);
// Plafond de zoom releve (8 -> 400, demande Rudy) : un cluster tres dense
// (ville avec beaucoup de depots) n'avait pas assez de marge pour se separer
// en sous-clusters avant que l'algorithme ne conclue "spiderfy" -- avec plus
// de marge, le clic zoome/re-zoome en cascade avant de spiderfier seulement
// ce qui reste vraiment coincident au zoom max.
// filter() : d3-zoom démarre son propre geste de pan dès le mousedown (avant
// même que le click du marqueur ne se déclenche) -- un stopPropagation() posé
// sur le handler 'click' du marqueur arrive donc TROP TARD pour l'empêcher.
// Ça produisait un évènement 'zoom' juste après l'ouverture du popup (même
// pour un simple clic, sans déplacement réel de souris), qui le refermait
// aussitôt. On empêche ici le geste de d3-zoom de démarrer quand la cible est
// un marqueur/cluster/le popup lui-même (leurs propres handlers gèrent déjà
// clic/carrousel/fermeture) -- comportement recommandé par la doc d3-zoom.
const zoomBehavior = zoom()
  .scaleExtent([1, 400])
  .filter((event) => !event.target.closest('.ew-item, .ew-popup'));

// Rayon de regroupement (en pixels ecran, converti en unites-donnees via /k
// au moment du calcul) et rayon de deploiement du spiderfy -- mêmes ordres de
// grandeur que carteMap.js (SPIDER_RADIUS = 80, maxClusterRadius = 40).
const CLUSTER_RADIUS_PX = 40;
const SPIDER_RADIUS_PX = 45;

// Largeur des contours (pays/globe/graticule), en pixels ECRAN CONSTANTS
// quel que soit le zoom -- demande Rudy (mesuré à 12px à 24x de zoom avant ce
// correctif, pour un stroke-width de .5 "unité donnée" fixe dans la feuille
// de style : ça grossit avec le zoom comme le reste du contenu du <g> zoomé,
// contrairement aux marqueurs qui compensent déjà via scale(1/k)). Fixé en JS
// (.style(), qui prime sur la classe CSS) plutôt qu'en dur dans le CSS.
// 1px partout (demande Rudy, encore trop épais à 2px).
const COUNTRY_STROKE_PX = 1;
const OUTLINE_STROKE_PX = 1;
const GRATICULE_STROKE_PX = 1;

function updateStrokeWidths(k) {
  landLayer.selectAll('.ew-country').style('stroke-width', `${COUNTRY_STROKE_PX / k}px`);
  graticuleLayer.selectAll('.ew-outline').style('stroke-width', `${OUTLINE_STROKE_PX / k}px`);
  graticuleLayer.selectAll('.ew-graticule-lines').style('stroke-width', `${GRATICULE_STROKE_PX / k}px`);
}

let fontBuffer = null;
async function loadFont() {
  if (fontBuffer) return fontBuffer;
  const res = await fetch('/assets/fonts/nocode.otf');
  fontBuffer = await res.arrayBuffer();
  return fontBuffer;
}

const snap = (value) => Math.round(value * 2000) / 2000;

function getSize() {
  const el = document.getElementById('equalearth-svg');
  return [el.clientWidth, el.clientHeight];
}

const FIT_MARGIN = 0.02;
// Objet "planète entière" (l'ovale complet de la projection, pôles compris)
// -- distinct des polygones de pays : l'Antarctique/l'Arctique n'atteignent
// pas les pôles géométriques exacts (marge d'océan), donc caler le cadrage
// sur les seuls contours de pays laissait un vide en haut/bas même en mode
// "cover" (pôles censés toucher les bords, cf. fitProjection). Le sphere
// donne l'emprise VRAIE du globe projeté, sur laquelle on cale à la fois le
// cadrage portrait et les bornes de pan (updateZoomBounds).
const SPHERE = { type: 'Sphere' };

// En portrait (écran plus haut que large -- mobile), le planisphère Eckert IV
// (~2:1) "contenu" dans un cadre étroit/haut (fitExtent classique, qui borne
// LES DEUX dimensions) restait minuscule au centre d'un grand vide en haut/
// bas : la carte semblait "flotter dans la page" (constaté par Rudy). On
// calque alors l'échelle sur la HAUTEUR seule (pôles qui touchent haut/bas de
// l'écran, comme demandé), quitte à ce que le monde déborde horizontalement
// (on pourra alors le parcourir latéralement au zoom minimum, cf.
// updateZoomBounds ci-dessous) -- au lieu de fitExtent, fitHeight (d3-geo).
// fitHeight cale par défaut le bord GAUCHE du contenu à x=0 (pas le centre) :
// on recentre nous-mêmes horizontalement après coup.
function fitProjection(landFeature) {
  const [w, h] = getSize();
  svg.attr('viewBox', `0 0 ${w} ${h}`);
  if (h > w) {
    projection.fitHeight(h * (1 - FIT_MARGIN * 2), SPHERE);
    const bounds = path.bounds(SPHERE);
    const cx = (bounds[0][0] + bounds[1][0]) / 2;
    const [tx, ty] = projection.translate();
    projection.translate([tx + (w / 2 - cx), ty + h * FIT_MARGIN]);
  } else {
    projection.fitExtent([[w * FIT_MARGIN, h * FIT_MARGIN], [w * (1 - FIT_MARGIN), h * (1 - FIT_MARGIN)]], landFeature);
  }
}

// extent() sur la boîte-écran [[0,0],[w,h]], mais translateExtent() sur
// l'UNION de cette boîte-écran et de l'emprise réelle du globe projeté
// (bornes du sphere) : en mode "contain" (desktop/paysage), le globe tient
// déjà dans [0,w]x[0,h] -- l'union vaut la boîte-écran, donc AUCUN pan
// possible à k=1 ("on voit la planète entière, empêche les mouvements",
// demande Rudy). En mode "cover" (portrait/mobile, cf. fitProjection
// ci-dessus), le globe DÉBORDE volontairement de [0,w] à gauche/droite --
// l'union devient alors plus grande que l'écran, ce qui autorise justement
// le pan latéral nécessaire pour atteindre les portions du monde coupées par
// le cadrage, même à k=1 (sinon elles seraient définitivement hors
// d'atteinte). Pas de logique de zoom à la main : translateExtent fait ça
// nativement dans les deux cas. Appelé à chaque fois que la taille/le
// cadrage du stage change.
function updateZoomBounds() {
  const [w, h] = getSize();
  zoomBehavior.extent([[0, 0], [w, h]]);
  const bounds = path.bounds(SPHERE);
  zoomBehavior.translateExtent([
    [Math.min(0, bounds[0][0]), Math.min(0, bounds[0][1])],
    [Math.max(w, bounds[1][0]), Math.max(h, bounds[1][1])],
  ]);
}

async function loadWorld() {
  const res = await fetch('/assets/data/countries-50m.json');
  const topo = await res.json();
  return feature(topo, topo.objects.countries);
}

async function fetchJson(url) {
  const res = await fetch(url);
  if (!res.ok) return [];
  return res.json();
}

function drawGraticule() {
  const graticule = geoGraticule().step([20, 20]);
  graticuleLayer.append('path').datum(graticule()).attr('class', 'ew-graticule-lines').attr('d', path);
  graticuleLayer.append('path').datum({ type: 'Sphere' }).attr('class', 'ew-outline').attr('d', path);
}

function drawLand(countries) {
  landLayer.selectAll('path').data(countries.features).join('path').attr('class', 'ew-country').attr('d', path);
}

// Noms de pays (demande Rudy) -- déjà dans countries-50m.json
// (properties.name, cf. world-atlas), pas de nouvelle donnée nécessaire.
// Classés par superficie ; aucun affiché à l'affichage complet du globe
// (k=1 pile), ils apparaissent dès qu'on commence à zoomer (minK juste
// au-dessus de 1), les plus petits progressivement ensuite.
function assignMinK(count, { growth }) {
  const arr = [];
  for (let rank = 0; rank < count; rank++) arr.push(1 + (rank + 1) * growth);
  return arr;
}

// Découpe un nom de pays trop long sur plusieurs lignes (à la volée, pas de
// mesure DOM réelle : simple répartition par mots, seuil en nombre de
// caractères -- suffisant pour ce style de carte épurée).
function wrapLabel(name, maxChars = 11) {
  const words = name.split(' ');
  if (name.length <= maxChars || words.length === 1) return [name];
  const lines = [];
  let current = '';
  for (const w of words) {
    const next = current ? `${current} ${w}` : w;
    if (current && next.length > maxChars) { lines.push(current); current = w; } else { current = next; }
  }
  if (current) lines.push(current);
  return lines;
}

function buildCountryLabels(countries) {
  const ranked = countries.features
    .map((f) => ({ name: f.properties?.name, area: geoArea(f), centroid: geoCentroid(f) }))
    .filter((c) => c.name && Number.isFinite(c.centroid[0]))
    .sort((a, b) => b.area - a.area);
  const minKs = assignMinK(ranked.length, { growth: 0.12 });
  return ranked.map((c, i) => ({
    name: c.name, lines: wrapLabel(c.name), lng: c.centroid[0], lat: c.centroid[1], minK: minKs[i],
  }));
}

function drawCountryLabels(labels) {
  const sel = labelLayer.selectAll('.ew-label--country')
    .data(labels, (d) => d.name)
    .join('text')
    .attr('class', 'ew-label ew-label--country');
  sel.each(function (d) {
    const el = select(this);
    el.selectAll('tspan').remove();
    const n = d.lines.length;
    d.lines.forEach((line, i) => {
      el.append('tspan').attr('x', 0).attr('dy', i === 0 ? `${-(n - 1) * 0.55}em` : '1.1em').text(line);
    });
  });
}

// Rayon approx. (px écran) de chaque type d'item affiché sur la carte --
// sert à la fois à décaler les noms de ville juste sous leur marqueur et à
// détecter les chevauchements label/marqueur (cf. renderLabels ci-dessous).
const ITEM_RADIUS_PX = { punchline: 8, ar: 9, map: 10, cluster: 20 };

// Ville la plus proche d'un point (lat/lng), si elle est à moins de
// CITY_MATCH_DEG -- comparaison brute en degrés (pas de vraie distance
// orthodromique). 0.5° avait été essayé pour éviter un rattachement trop
// approximatif (Le Puy-en-Velay, absent de toutes les bases Natural Earth,
// étiqueté "Saint-Étienne" à ~75 km) mais ça cachait aussi des villes
// pourtant pertinentes un peu plus loin (Bordeaux à ~95 km d'un cluster,
// Saint-Étienne à ~72 km d'un autre, cf. demande Rudy) -- remonté à 1° (~110
// km) : mieux vaut un nom de ville "la plus proche connue" même pas
// parfaitement exact que pas de nom du tout sur un cluster.
const CITY_MATCH_DEG = 1;
function findNearestCity(places, lat, lng) {
  let best = null;
  let bestD = Infinity;
  for (const p of places) {
    const d = Math.hypot(p.lat - lat, p.lng - lng);
    if (d < bestD) { bestD = d; best = p; }
  }
  return bestD <= CITY_MATCH_DEG ? best : null;
}

// Seuil de zoom des villes "libres" (non accrochées à un marqueur, cf. plus
// bas) basé sur leur SCALERANK Natural Earth -- un champ FAIT pour ça (0 =
// très grandes villes/capitales, 10 = villes secondaires), donc préféré à un
// classement par population maison comme pour les pays.
const CITY_LABEL_GROWTH = 0.55;

// Estimation grossière (pas de mesure DOM réelle) de la largeur/hauteur
// écran d'un texte, pour le test de chevauchement -- suffisant ici, on n'a
// pas besoin d'un pixel près.
function labelBoxPx(text, fontSizePx, lines = 1) {
  return { w: text.length * fontSizePx * 0.6, h: fontSizePx * 1.2 * lines };
}
// a = rectangle centré sur ax,ay ; b = cercle centré sur bx,by de rayon br.
function boxCircleOverlap(ax, ay, aw, ah, bx, by, br) {
  return Math.abs(ax - bx) < aw / 2 + br && Math.abs(ay - by) < ah / 2 + br;
}
function boxesOverlap(ax, ay, aw, ah, bx, by, bw, bh) {
  return Math.abs(ax - bx) < (aw + bw) / 2 && Math.abs(ay - by) < (ah + bh) / 2;
}

// Repositionne les noms de pays/villes à chaque tick de zoom (même
// compensation scale(1/k) que les marqueurs, taille constante à l'écran),
// bascule leur visibilité selon leur seuil de zoom, ET évite tout
// chevauchement -- label/marqueur ET label/label (demande Rudy : "une règle
// pour éviter les chevauchements nom de ville ou pays / point-étoile-carré")
// -- via un algorithme glouton simple : chaque élément est placé dans
// l'ordre de priorité (marqueurs > pays > villes accrochées à un marqueur >
// villes libres, les plus grandes d'abord) et sauté s'il chevaucherait déjà
// quelque chose de posé.
//
// Villes : deux sources fusionnées --
//  1. "accrochées" à un marqueur/cluster réellement affiché, toujours
//     montrées dès qu'on zoome un peu (demande Rudy : nommer les endroits où
//     il y a du contenu, même une petite ville) ;
//  2. "libres", révélées par zoom selon leur SCALERANK, pour étoffer la
//     carte même là où il n'y a pas de contenu déposé (demande Rudy :
//     "trouve une règle qui étoffe un peu la carte à ce niveau-là").
// Aucune des deux à la vue mondiale complète (k=1 pile).
function renderLabels(k, renderItems, places) {
  const countryFontPx = 9;
  const cityFontPx = 10;

  // Garde-fou viewport : sans ça, les villes "libres" (cf. plus bas) étaient
  // testées sur les 7000+ entrées de places.json à CHAQUE tick, y compris
  // celles à l'autre bout du monde, hors écran -- bug constaté (5966 labels
  // simultanés à k=16, la quasi-totalité du fichier) en plus d'être un vrai
  // problème de performance (chaque candidat testé contre tous les éléments
  // déjà posés). On ne considère que ce qui tombe dans le cadre visible
  // (+ petite marge) AVANT le test de chevauchement.
  const [stageW, stageH] = getSize();
  const transform = zoomTransform(svg.node());
  const VIEWPORT_MARGIN_PX = 80;
  function projectedPointInViewport(proj) {
    const sx = transform.x + proj[0] * k;
    const sy = transform.y + proj[1] * k;
    return sx >= -VIEWPORT_MARGIN_PX && sx <= stageW + VIEWPORT_MARGIN_PX
      && sy >= -VIEWPORT_MARGIN_PX && sy <= stageH + VIEWPORT_MARGIN_PX;
  }

  const occupied = renderItems.map((item) => {
    const r = ITEM_RADIUS_PX[item.type || 'cluster'] / k;
    return { x: item.x, y: item.y, r };
  });
  const fitsWithoutOverlap = (x, y, w, h) => !occupied.some((o) => (o.r != null
    ? boxCircleOverlap(x, y, w, h, o.x, o.y, o.r)
    : boxesOverlap(x, y, w, h, o.x, o.y, o.w, o.h)));

  // ── Pays ──
  const countrySel = labelLayer.selectAll('.ew-label--country')
    .attr('transform', (d) => {
      const p = projection([d.lng, d.lat]);
      return p ? `translate(${p[0]},${p[1]}) scale(${1 / k})` : null;
    });
  countrySel.each(function (d) {
    const el = select(this);
    const p = projection([d.lng, d.lat]);
    if (k < d.minK || !p) { el.style('display', 'none'); return; }
    const box = labelBoxPx(d.name, countryFontPx, d.lines.length);
    const w = box.w / k;
    const h = box.h / k;
    if (!fitsWithoutOverlap(p[0], p[1], w, h)) { el.style('display', 'none'); return; }
    el.style('display', null);
    occupied.push({ x: p[0], y: p[1], w, h });
  });

  // ── Villes ──
  const cityItems = [];
  const usedCityNames = new Set();
  if (k > 1.01) {
    for (const item of renderItems) {
      if (typeof item.lat !== 'number' || typeof item.lng !== 'number') continue;
      // renderItems couvre TOUS les marqueurs du monde (le clustering ne
      // filtre pas par zone visible), pas seulement ceux à l'écran -- sans
      // ce garde-fou, chaque marqueur hors-champ (potentiellement à l'autre
      // bout du monde) déclenchait quand même une recherche de ville la plus
      // proche et un élément de label, invisible mais pour rien.
      if (!projectedPointInViewport([item.x, item.y])) continue;
      const city = findNearestCity(places, item.lat, item.lng);
      if (!city || usedCityNames.has(city.name)) continue;
      usedCityNames.add(city.name);
      const radius = ITEM_RADIUS_PX[item.type || 'cluster'];
      const offsetPx = radius + 10;
      cityItems.push({ name: city.name, x: item.x, y: item.y, offsetPx });
      const box = labelBoxPx(city.name, cityFontPx);
      occupied.push({ x: item.x, y: item.y + offsetPx / k, w: box.w / k, h: box.h / k });
    }
    // Villes "libres" : seulement les CAPITALES (demande Rudy -- avec les
    // 7000+ entrées du jeu de données, même en étageant par SCALERANK il y
    // en avait trop à l'écran à certains niveaux de zoom). Les villes
    // accrochées à un marqueur, elles, restent affichées quelle que soit
    // leur importance (boucle au-dessus).
    for (const p of places) {
      if (!p.cap || usedCityNames.has(p.name)) continue;
      if (k < 1 + (p.rank + 1) * CITY_LABEL_GROWTH) continue;
      const proj = projection([p.lng, p.lat]);
      if (!proj || !projectedPointInViewport(proj)) continue;
      const box = labelBoxPx(p.name, cityFontPx);
      const w = box.w / k;
      const h = box.h / k;
      if (!fitsWithoutOverlap(proj[0], proj[1], w, h)) continue;
      usedCityNames.add(p.name);
      occupied.push({ x: proj[0], y: proj[1], w, h });
      cityItems.push({ name: p.name, x: proj[0], y: proj[1], offsetPx: 0 });
    }
  }

  labelLayer.selectAll('.ew-label--city')
    .data(cityItems, (d) => d.name)
    .join('text')
    .attr('class', 'ew-label ew-label--city')
    // scale(1/k) ANNULE le scale(k) du <g> root (comme pour les marqueurs et
    // les noms de pays) -- sans ça le texte grossirait avec le zoom au lieu
    // de garder une taille constante à l'écran. Le translate() imbriqué qui
    // suit s'exprime alors directement en pixels écran (même principe que
    // les formes des marqueurs, cf. shapeGroup()).
    .attr('transform', (d) => `translate(${d.x},${d.y}) scale(${1 / k}) translate(0,${d.offsetPx})`)
    .text((d) => d.name);
}

// ── Regroupement par coordonnées EXACTES (même logique que carteMap.js) :
// ce sont les "feuilles" du clustering, chacune avec son popup (carrousel si
// plusieurs éléments au même endroit précis).
function groupByLatLng(items, getLat, getLng) {
  const groups = new Map();
  for (const item of items) {
    const lat = getLat(item);
    const lng = getLng(item);
    if (typeof lat !== 'number' || typeof lng !== 'number') continue;
    const key = `${snap(lat)},${snap(lng)}`;
    if (!groups.has(key)) groups.set(key, { lat, lng, items: [] });
    groups.get(key).items.push(item);
  }
  return [...groups.values()];
}

// ── Popup maison (pas d'équivalent L.popup en D3) ──────────────────────────
let popupCarousel = null;
// Le pan programmatique (autoPan, cf. openPopup) déclenche lui aussi un
// évènement 'zoom' -- sans ce garde-fou, le handler 'zoom' fermerait le popup
// que l'autoPan est justement en train de repositionner pour le garder visible.
let suppressCloseOnZoom = false;

function closePopup() {
  popupEl.hidden = true;
  popupCarousel = null;
}

function buildCarouselHtml(items, index) {
  return items[index].popupHtml
    + (items.length > 1
      ? '<div class="popup-nav-row">'
        + '<button class="popup-btn-prev" type="button">&#8249;</button>'
        + `<span class="popup-nav-count">${index + 1} / ${items.length}</span>`
        + '<button class="popup-btn-next" type="button">&#8250;</button>'
        + '</div>'
      : '');
}

function renderPopup(items, index) {
  popupEl.querySelector('.ew-popup-content').innerHTML = buildCarouselHtml(items, index);
  popupEl.querySelector('.popup-btn-prev')?.addEventListener('click', (e) => {
    e.stopPropagation();
    popupCarousel.index = (popupCarousel.index - 1 + items.length) % items.length;
    renderPopup(items, popupCarousel.index);
  });
  popupEl.querySelector('.popup-btn-next')?.addEventListener('click', (e) => {
    e.stopPropagation();
    popupCarousel.index = (popupCarousel.index + 1) % items.length;
    renderPopup(items, popupCarousel.index);
  });
}

// Attend qu'une éventuelle image du popup (photo AR/carte) ait fini de
// charger avant de mesurer sa taille -- sans ça, getBoundingClientRect()
// mesure le popup à sa hauteur AVANT chargement (texte/marge seuls), le
// positionne en conséquence, puis l'image apparaît et fait grossir le popup
// hors-cadre sans que rien ne le repositionne (bug constate : popup-photo
// tronqué en bas malgre le clamp deja en place). Timeout de secours (image
// cassee/lente) pour ne jamais bloquer indefiniment.
function waitForPopupImages() {
  const imgs = [...popupEl.querySelectorAll('img')];
  if (imgs.length === 0) return Promise.resolve();
  return Promise.all(imgs.map((img) => (img.complete ? Promise.resolve() : new Promise((resolve) => {
    img.addEventListener('load', resolve, { once: true });
    img.addEventListener('error', resolve, { once: true });
    setTimeout(resolve, 1500);
  }))));
}

// Repositionne le popup, et si meme la meilleure position possible deborde
// du cadre visible, PANNE LA CARTE (comme l'autoPan de Leaflet, cf.
// getPopupAutoPanOpts()/regle deja en place sur la vraie carte) au lieu de
// juste clamp le popup dans une place insuffisante -- le marqueur ET son
// popup restent alors visibles ensemble.
async function openPopup(markerEl, items, startIndex = 0) {
  popupCarousel = { index: startIndex };
  renderPopup(items, startIndex);
  popupEl.hidden = false;
  await waitForPopupImages();
  if (popupEl.hidden) return; // fermé entre-temps (autre clic, etc.)

  const stageEl = document.querySelector('.ew-stage');
  const popupRect = popupEl.getBoundingClientRect();
  const stageRect = stageEl.getBoundingClientRect();
  const markerRect = markerEl.getBoundingClientRect();

  let left = markerRect.left - stageRect.left + markerRect.width / 2 - popupRect.width / 2;
  let top = markerRect.top - stageRect.top - popupRect.height - 14;
  let flippedBelow = false;
  if (top < 8) { top = markerRect.bottom - stageRect.top + 14; flippedBelow = true; }
  // Triangle indiquant le marqueur rattaché (cf. .ew-popup-tip / .leaflet-popup-tip
  // sur la vraie carte) : pointe vers le bas par défaut (popup au-dessus du
  // marqueur), vers le haut quand le popup est basculé en dessous.
  popupEl.classList.toggle('ew-popup--flipped', flippedBelow);

  const overflowLeft = 8 - left;
  const overflowRight = (left + popupRect.width) - (stageRect.width - 8);
  const overflowTop = 8 - top;
  const overflowBottom = (top + popupRect.height) - (stageRect.height - 8);
  const panX = overflowLeft > 0 ? overflowLeft : overflowRight > 0 ? -overflowRight : 0;
  const panY = overflowTop > 0 ? overflowTop : overflowBottom > 0 ? -overflowBottom : 0;

  if (panX !== 0 || panY !== 0) {
    const k = zoomTransform(svg.node()).k;
    suppressCloseOnZoom = true;
    svg.transition().duration(380).call(zoomBehavior.translateBy, panX / k, panY / k)
      .on('end.suppressClose', () => { suppressCloseOnZoom = false; });
    // Le pan déplace tout le contenu du <g> zoomé (marqueur compris) de
    // exactement (panX, panY) écran -- le popup, lui, n'est pas dans ce <g>
    // (overlay HTML classique), donc on applique le même décalage directement
    // plutôt que de relire le DOM (la transition anime sur plusieurs frames,
    // le lire tout de suite après .call() ne donnerait pas la position finale).
    left += panX;
    top += panY;
  }
  popupEl.style.left = `${left}px`;
  popupEl.style.top = `${top}px`;
}

popupCloseBtn.addEventListener('click', (e) => { e.stopPropagation(); closePopup(); });

function starPoints(r) {
  const pts = [];
  for (let i = 0; i < 10; i++) {
    const radius = i % 2 === 0 ? r : r * 0.42;
    const angle = (Math.PI / 5) * i - Math.PI / 2;
    pts.push(`${(radius * Math.cos(angle)).toFixed(2)},${(radius * Math.sin(angle)).toFixed(2)}`);
  }
  return pts.join(' ');
}

// ── Préparation des données : une "feuille" par point exact, tous types
// mélangés (punchlines + photos AR + photos carte), chacune avec sa position
// projetée (espace "données", indépendante du zoom) et son contenu popup. ──
async function buildLeafMarkers({ punchlines, arPhotos, mapPhotos }) {
  const leaves = [];

  const punchlineGroups = groupByLatLng(punchlines, (r) => r.lat, (r) => r.lng);
  const buf = await loadFont().catch(() => null);
  for (const group of punchlineGroups) {
    const itemsWithHtml = [];
    for (const item of group.items) {
      try {
        const svgStr = buf ? await resolveStaticSvg({
          script: item.script, text: item.inputText, knockoutSvg: item.knockoutSvg,
          generateSVG, fontBuffer: buf, color: '#ffffff',
        }) : '';
        const pseudo = item.pseudo ? `<div class="popup-pseudo">${escapeHtml(item.pseudo)}</div>` : '';
        itemsWithHtml.push({ popupHtml: `<div class="popup-svg-wrap">${svgStr}</div>${pseudo}` });
      } catch {
        const pseudo = item.pseudo ? `<div class="popup-pseudo">${escapeHtml(item.pseudo)}</div>` : '';
        itemsWithHtml.push({ popupHtml: `<strong>${escapeHtml(item.inputText)}</strong>${pseudo}` });
      }
    }
    const p = projection([group.lng, group.lat]);
    if (p) leaves.push({ type: 'punchline', lat: group.lat, lng: group.lng, p, popupItems: itemsWithHtml });
  }

  const photoTypes = [
    { rows: arPhotos, apiPath: '/api/ar-photos', type: 'ar' },
    { rows: mapPhotos, apiPath: '/api/map-photos', type: 'map' },
  ];
  for (const { rows, apiPath, type } of photoTypes) {
    const groups = groupByLatLng(rows, (r) => r.lat, (r) => r.lng);
    for (const group of groups) {
      const popupItems = group.items.map((photo) => {
        const pseudo = photo.pseudo ? `<div class="popup-pseudo">${escapeHtml(photo.pseudo)}</div>` : '';
        return {
          photoKey: `${type}-${photo.id}`,
          popupHtml: `<div class="popup-photo-wrap"><img src="${apiPath}/${photo.id}/image?size=web" alt="photo"></div>${pseudo}`,
        };
      });
      const p = projection([group.lng, group.lat]);
      if (p) leaves.push({ type, lat: group.lat, lng: group.lng, p, popupItems });
    }
  }

  return leaves;
}

// ── Clustering glouton par proximité écran (recalculé à chaque zoom) ──
function computeClusters(leaves, k) {
  const radius = CLUSTER_RADIUS_PX / k;
  const clusters = [];
  for (const leaf of leaves) {
    let target = null;
    for (const c of clusters) {
      const dx = c.cx - leaf.p[0];
      const dy = c.cy - leaf.p[1];
      if (Math.hypot(dx, dy) < radius) { target = c; break; }
    }
    if (target) {
      target.members.push(leaf);
      target.cx = target.members.reduce((s, m) => s + m.p[0], 0) / target.members.length;
      target.cy = target.members.reduce((s, m) => s + m.p[1], 0) / target.members.length;
    } else {
      clusters.push({ cx: leaf.p[0], cy: leaf.p[1], members: [leaf] });
    }
  }
  return clusters;
}

function clusterSpanPx(cluster, k) {
  // Etendue du cluster en pixels écran courants -- sert à décider si un zoom
  // suffirait à séparer les membres (sinon : spiderfy direct, comme
  // carteMap.js quand bounds.getNorth()-bounds.getSouth() < 0.01°).
  let maxDist = 0;
  for (const m of cluster.members) {
    maxDist = Math.max(maxDist, Math.hypot(m.p[0] - cluster.cx, m.p[1] - cluster.cy));
  }
  return maxDist * k;
}

function spiderfyPositions(cluster) {
  const n = cluster.members.length;
  return cluster.members.map((m, i) => {
    const angle = (2 * Math.PI * i) / n - Math.PI / 2;
    return { member: m, sx: Math.cos(angle) * SPIDER_RADIUS_PX, sy: Math.sin(angle) * SPIDER_RADIUS_PX };
  });
}

// computeClusters() reconstruit des objets cluster a CHAQUE appel (nouveau
// tableau .members a chaque zoom) : comparer par reference ne marcherait
// jamais d'un rendu a l'autre. On identifie donc un cluster par le contenu de
// ses membres (triable, stable) plutot que par l'objet lui-meme -- le
// cluster "actuellement deploye" est retrouve par CETTE clé, pas par ===.
function leafKey(leaf) { return `${leaf.type}|${leaf.lat}|${leaf.lng}`; }
function clusterKey(cluster) { return cluster.members.map(leafKey).sort().join(','); }

// Tailles alignées sur les icônes de la vraie carte (carte-marker-icon,
// SQUARE_SVG/STAR_SVG dans carteMap.js : cercle/carré 12px, étoile ~14px de
// pointe à pointe) -- demande Rudy ("les icônes ... me paraissent plus
// petites sur equalearth").
function shapeGroup(sel, type) {
  if (type === 'punchline') {
    sel.append('circle').attr('r', 6);
  } else if (type === 'ar') {
    sel.append('rect').attr('x', -6).attr('y', -6).attr('width', 12).attr('height', 12);
  } else if (type === 'map') {
    sel.append('polygon').attr('points', starPoints(7));
  }
  return sel;
}

// ── État global du rendu (recalculé/redessiné à chaque tick de zoom) ──
let allLeaves = [];
let placesData = []; // villes (places.json), pour l'accrochage des noms de ville dans renderLabels()
let spiderfiedKey = null; // clé (clusterKey) du cluster actuellement déployé, ou null

// ── Légende repliable sur mobile (même bouton "i"/comportement que
// createMapLegendControl() dans carteMap.js -- classes carte-map-legend*
// partagées, cf. equalearth.html, donc même icône et même effet hover). ──
const legendPanel = document.querySelector('.carte-map-legend');
const legendToggleBtn = document.getElementById('equalearth-legend-toggle');
legendToggleBtn.addEventListener('click', (e) => {
  e.stopPropagation();
  const isOpen = legendPanel.classList.toggle('is-open');
  legendToggleBtn.classList.toggle('is-active', isOpen);
  legendToggleBtn.setAttribute('aria-expanded', String(isOpen));
  legendToggleBtn.setAttribute('aria-label', isOpen ? 'Masquer la légende' : 'Afficher la légende');
});

// ── "Tout voir"/"Retour" (même bouton que resetViewControl de carteMap.js) ──
const resetBtn = document.getElementById('equalearth-reset');
let viewHistory = [];

function isHomeTransform(t) {
  return Math.abs(t.k - 1) < 0.01 && Math.abs(t.x) < 2 && Math.abs(t.y) < 2;
}

// Deux fonctions distinctes : purger l'historique quand on est "chez soi" ne
// doit arriver qu'une fois le geste/la transition VRAIMENT stabilisé — sur
// chaque tick ('zoom', appelé aussi au tout début d'une transition animée,
// où le transform interpolé vaut encore ~l'identité de depart) ça viderait
// l'historique qu'on vient de pousser avant meme que l'animation ne parte
// (bug constate : le bouton restait bloque sur "Tout voir" au lieu de
// passer a "Retour" apres un zoom sur cluster).
function updateResetButtonLabel() {
  const atHome = isHomeTransform(zoomTransform(svg.node()));
  resetBtn.classList.toggle('is-visible', !atHome);
  resetBtn.textContent = viewHistory.length > 0 ? 'Retour' : 'Tout voir';
}

function pruneHistoryIfHome() {
  if (isHomeTransform(zoomTransform(svg.node()))) viewHistory = [];
  updateResetButtonLabel();
}

resetBtn.addEventListener('click', (e) => {
  e.stopPropagation();
  const prev = viewHistory.pop();
  updateResetButtonLabel(); // reflète immédiatement le pop (avant même la fin de l'anim)
  svg.transition().duration(900).call(zoomBehavior.transform, prev || zoomIdentity);
});

function zoomToBounds(members) {
  const xs = members.map((m) => m.p[0]);
  const ys = members.map((m) => m.p[1]);
  const minX = Math.min(...xs), maxX = Math.max(...xs);
  const minY = Math.min(...ys), maxY = Math.max(...ys);
  const [w, h] = getSize();
  const PAD = 80;
  const spanX = Math.max(maxX - minX, 1);
  const spanY = Math.max(maxY - minY, 1);
  let k = Math.min((w - PAD * 2) / spanX, (h - PAD * 2) / spanY);
  k = Math.max(zoomBehavior.scaleExtent()[0], Math.min(zoomBehavior.scaleExtent()[1], k));
  const cx = (minX + maxX) / 2;
  const cy = (minY + maxY) / 2;
  const tx = w / 2 - k * cx;
  const ty = h / 2 - k * cy;
  viewHistory.push(zoomTransform(svg.node())); // pile de navigation, comme viewHistory dans carteMap.js
  svg.transition().duration(900).call(zoomBehavior.transform, zoomIdentity.translate(tx, ty).scale(k));
}

// animate=true : utilisé pour les changements d'ÉTAT (ouverture/fermeture
// d'un spiderfy) plutôt que pour un vrai geste de pan/zoom continu -- dans ce
// cas on anime la position des marqueurs (sinon saut instantané, jugé "trop
// brusque"). Pendant un pan/zoom en cours (tick 'zoom', appelé en continu),
// on garde animate=false : transitionner à chaque frame d'un geste déjà animé
// par le zoom lui-même ferait juste ramer, pas glisser.
function render(animate = false) {
  const k = zoomTransform(svg.node()).k;
  updateStrokeWidths(k);
  const clusters = computeClusters(allLeaves, k);
  const activeCluster = spiderfiedKey ? clusters.find((c) => clusterKey(c) === spiderfiedKey) : null;
  if (spiderfiedKey && !activeCluster) spiderfiedKey = null; // le zoom a change : le regroupement d'avant n'existe plus

  const legSel = legsLayer.selectAll('line').data(activeCluster ? spiderfyPositions(activeCluster) : [], (d) => leafKey(d.member));
  // Suppression immédiate (pas de fondu) pendant un pan/zoom continu : un
  // élément qui sort du join (exit) n'est plus mis à jour par les ticks
  // suivants -- si le fondu dure pendant que le zoom continue d'animer, sa
  // propre compensation scale(1/k) reste figée à l'ancien k pendant que le
  // <g> parent continue de grossir, et il apparaît ÉNORME le temps du fondu
  // avant de disparaître (bug constaté : cluster à 450px de large au lieu de
  // 30px, pendant un zoom animé). Le fondu n'est donc utilisé QUE pour un
  // changement d'état ponctuel (spiderfy), où le zoom lui-même est stable.
  const legExit = legSel.exit();
  if (animate) {
    legExit.transition().duration(260).style('opacity', 0).remove();
  } else {
    legExit.remove();
  }
  const legEnter = legSel.enter().append('line').attr('class', 'ew-spider-leg').style('opacity', 0);
  const legMerged = legEnter.merge(legSel);
  (animate ? legMerged.transition().duration(420) : legMerged)
    .attr('x1', activeCluster ? activeCluster.cx : 0).attr('y1', activeCluster ? activeCluster.cy : 0)
    .attr('x2', (d) => activeCluster.cx + d.sx / k)
    .attr('y2', (d) => activeCluster.cy + d.sy / k)
    .attr('stroke-width', 1 / k)
    .style('opacity', activeCluster ? 0.5 : 0);

  const renderItems = [];
  for (const cluster of clusters) {
    if (activeCluster && cluster === activeCluster) {
      const positions = spiderfyPositions(cluster);
      for (const { member, sx, sy } of positions) {
        renderItems.push({
          kind: 'leaf', type: member.type, popupItems: member.popupItems,
          x: cluster.cx + sx / k, y: cluster.cy + sy / k, key: `spider-${leafKey(member)}`,
          lat: member.lat, lng: member.lng,
        });
      }
    } else if (cluster.members.length === 1) {
      const m = cluster.members[0];
      renderItems.push({
        kind: 'leaf', type: m.type, popupItems: m.popupItems,
        x: m.p[0], y: m.p[1], key: leafKey(m),
        lat: m.lat, lng: m.lng,
      });
    } else {
      // Centre géographique approx. du cluster (moyenne simple des membres)
      // -- sert uniquement à retrouver la ville la plus proche pour le
      // label, pas besoin d'un vrai centroïde pondéré ici.
      const lat = cluster.members.reduce((s, m) => s + m.lat, 0) / cluster.members.length;
      const lng = cluster.members.reduce((s, m) => s + m.lng, 0) / cluster.members.length;
      renderItems.push({
        kind: 'cluster', cluster, x: cluster.cx, y: cluster.cy,
        key: `cluster-${clusterKey(cluster)}`, lat, lng,
      });
    }
  }

  renderLabels(k, renderItems, placesData);

  const sel = markerLayer.selectAll('.ew-item')
    .data(renderItems, (d) => d.key)
    .join(
      (enter) => {
        const g = enter.append('g')
          .attr('class', (d) => `ew-item ${d.kind === 'cluster' ? 'ew-cluster' : `ew-marker ew-marker--${d.type}`}`)
          .attr('transform', (d) => `translate(${d.x},${d.y}) scale(${1 / k})`)
          .style('opacity', 0);
        g.each(function (d) {
          const node = select(this);
          if (d.kind === 'cluster') {
            // Rayon/positions calqués sur .carte-cluster-icon (35px de large,
            // glyphes à 14px/12px, cf. style.css) -- demande Rudy.
            node.append('circle').attr('class', 'ew-cluster-bg').attr('r', 17.5);
            node.append('text').attr('class', 'ew-cluster-shape ew-cluster-shape--circle').attr('x', -6).attr('y', 7).text('●');
            node.append('text').attr('class', 'ew-cluster-shape ew-cluster-shape--square').attr('x', 5).attr('y', 7).text('■');
            node.append('text').attr('class', 'ew-cluster-shape ew-cluster-shape--star').attr('x', -1).attr('y', -3.5).text('★');
          } else {
            shapeGroup(node, d.type);
          }
        });
        return g;
      },
      (update) => update,
      // Fade-out avant suppression -- MAIS seulement pour un changement
      // d'état (spiderfy) : pendant un pan/zoom continu (animate=false), un
      // exit fige la compensation scale(1/k) de l'élément à l'ancien zoom
      // pendant que le <g> parent continue d'animer -- il gonfle énormément
      // le temps du fondu (cf. commentaire détaillé plus haut sur les legs).
      (exit) => (animate ? exit.transition().duration(260).style('opacity', 0).remove() : exit.remove()),
    );

  // Nouveaux éléments (fade-in, toujours doux -- ralenti, demande Rudy : les
  // apparitions de photos/punchlines paraissaient saccadées) + repositionnement
  // des éléments existants -- animé seulement pour un changement d'état
  // (spiderfy), instantané pendant un pan/zoom continu (cf. commentaire sur
  // render()). Deux transitions NOMMÉES distinctes ('ew-fade'/'ew-move') :
  // appeler .transition() deux fois avec le nom par défaut interromprait la
  // première (jamais menée à son terme -- bug constaté : marqueurs bloqués à
  // une opacité quasi nulle, invisibles bien qu'au bon endroit).
  sel.transition('ew-fade').duration(animate ? 420 : 320).style('opacity', 1);
  if (animate) {
    sel.transition('ew-move').duration(420).attr('transform', (d) => `translate(${d.x},${d.y}) scale(${1 / k})`);
  } else {
    sel.attr('transform', (d) => `translate(${d.x},${d.y}) scale(${1 / k})`);
  }

  sel.on('click', function (event, d) {
    event.stopPropagation();
    if (d.kind === 'cluster') {
      // Teste l'étendue au zoom MAX (pas le zoom courant) : un cluster ne
      // mérite un spiderfy que si zoomer au maximum ne suffirait de toute
      // façon pas à séparer visuellement ses membres (équivalent D3 du test
      // géographique fixe de carteMap.js, bounds < 0.01°) -- sinon on
      // sous-estimait presque toujours l'étendue réelle (le rayon de
      // regroupement lui-même borne déjà le span au zoom courant, donc le
      // comparer à CE zoom-là déclenchait le spiderfy quasi systématiquement).
      const maxK = zoomBehavior.scaleExtent()[1];
      const spanAtMaxZoom = clusterSpanPx(d.cluster, maxK);
      if (spanAtMaxZoom < SPIDER_RADIUS_PX) {
        spiderfiedKey = clusterKey(d.cluster);
        render(true);
      } else {
        zoomToBounds(d.cluster.members);
      }
    } else {
      // Cliquer un membre déployé d'un spiderfy ne doit PAS le refermer --
      // sur la vraie carte, la "patte" reste visible et les autres membres
      // du cluster restent affichés autour du popup ouvert (cf. le mécanisme
      // preclick/isPopupOpen de createClusterLayerIfAvailable() dans
      // carteMap.js, qui empêche justement ce même clic de désempiler le
      // cluster). Seul un clic sur une feuille NON déployée (un marqueur
      // isolé ailleurs) ferme un éventuel spiderfy en cours.
      if (!d.key.startsWith('spider-')) spiderfiedKey = null;
      openPopup(this, d.popupItems);
    }
  });
}

// Facteur de zoom par clic +/- -- même esprit que L.control.zoom (topright,
// cf. carteMap.js) mais reconstruit en SVG/D3 (pas de contrôle Leaflet ici).
const ZOOM_STEP_FACTOR = 1.6;
const zoomInBtn = document.getElementById('equalearth-zoom-in');
const zoomOutBtn = document.getElementById('equalearth-zoom-out');

function setupZoom() {
  zoomBehavior
    .on('zoom', (event) => {
      root.attr('transform', event.transform);
      if (!suppressCloseOnZoom) closePopup();
      render();
      updateResetButtonLabel();
    })
    .on('end', pruneHistoryIfHome);
  svg.call(zoomBehavior);
  svg.call(zoomBehavior.transform, zoomIdentity);
  svg.on('click', () => {
    closePopup();
    if (spiderfiedKey) { spiderfiedKey = null; render(true); }
  });

  zoomInBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    svg.transition().duration(480).call(zoomBehavior.scaleBy, ZOOM_STEP_FACTOR);
  });
  zoomOutBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    svg.transition().duration(480).call(zoomBehavior.scaleBy, 1 / ZOOM_STEP_FACTOR);
  });
}

// ── Paramètre /equalearth?photo=ar-12 (ex-lien "Voir sur la carte" d'Escales,
// qui pointe désormais vers /carte, cf. carteMap.js -- gardé pour les URL) :
// centre la carte sur la photo, au plus petit zoom où son marqueur n'est
// plus regroupé (sinon zoom plafond + déploiement du groupe), puis ouvre son
// popup directement sur cette photo. Zoom plafonné pour garder un peu de
// contexte géographique (au-delà, on ne voit plus qu'un bout de côte). ──
const PHOTO_FOCUS_MAX_K = 64;
function findPhotoLeaf(photoKey) {
  for (const leaf of allLeaves) {
    const index = (leaf.popupItems || []).findIndex((item) => item.photoKey === photoKey);
    if (index !== -1) return { leaf, index };
  }
  return null;
}

function clusterOfLeaf(leaf, k) {
  return computeClusters(allLeaves, k).find((c) => c.members.some((m) => leafKey(m) === leafKey(leaf)));
}

function focusPhotoFromUrl() {
  const photoKey = new URLSearchParams(window.location.search).get('photo');
  const found = photoKey && findPhotoLeaf(photoKey);
  if (!found) return;
  const maxK = Math.min(PHOTO_FOCUS_MAX_K, zoomBehavior.scaleExtent()[1]);
  let k = 8;
  while (k < maxK && clusterOfLeaf(found.leaf, k)?.members.length > 1) k *= 2;
  k = Math.min(k, maxK);
  const [w, h] = getSize();
  const target = zoomIdentity.translate(w / 2 - k * found.leaf.p[0], h / 2 - k * found.leaf.p[1]).scale(k);
  svg.transition().duration(1200).call(zoomBehavior.transform, target).on('end', () => {
    // allLeaves a pu être reconstruit entre-temps (ResizeObserver) : on
    // retrouve la feuille dans la version courante.
    const current = findPhotoLeaf(photoKey);
    if (!current) return;
    const kNow = zoomTransform(svg.node()).k;
    const cluster = clusterOfLeaf(current.leaf, kNow);
    let markerKey = leafKey(current.leaf);
    let delay = 0;
    if (cluster && cluster.members.length > 1) {
      spiderfiedKey = clusterKey(cluster);
      render(true);
      markerKey = `spider-${markerKey}`;
      delay = 450; // fin de l'animation de déploiement avant de mesurer
    }
    setTimeout(() => {
      const node = markerLayer.selectAll('.ew-item').filter((d) => d.key === markerKey).node();
      if (node) openPopup(node, current.leaf.popupItems, current.index);
    }, delay);
  });
}

async function init() {
  const [world, places, punchlines, arPhotos] = await Promise.all([
    loadWorld(),
    fetchJson('/assets/data/places.json'),
    fetchJson('/api/generations').then((rows) => rows.filter((r) => typeof r.lat === 'number' && typeof r.lng === 'number')),
    fetchJson('/api/ar-photos'),
  ]);
  placesData = places;
  let mapPhotos = await fetchJson('/api/map-photos');

  fitProjection(world);
  updateZoomBounds();
  drawGraticule();
  drawLand(world);
  drawCountryLabels(buildCountryLabels(world));
  allLeaves = await buildLeafMarkers({ punchlines, arPhotos, mapPhotos });
  setupZoom();
  render();

  // ResizeObserver plutôt que l'évènement 'resize' de window : recentre la
  // carte pour TOUT changement de taille du conteneur (redimensionnement de
  // fenêtre, rotation mobile, barre d'adresse qui se réduit/agrandit...),
  // pas seulement un vrai resize de fenêtre -- demande Rudy ("quand la carte
  // est entière, qu'elle se recentre automatiquement dans la page").
  const stageEl = document.querySelector('.ew-stage');
  const resizeObserver = new ResizeObserver(() => {
    const wasHome = isHomeTransform(zoomTransform(svg.node()));
    fitProjection(world);
    updateZoomBounds();
    landLayer.selectAll('path').attr('d', path);
    graticuleLayer.selectAll('path').attr('d', path);
    if (wasHome) svg.call(zoomBehavior.transform, zoomIdentity);
    closePopup();
    spiderfiedKey = null;
    buildLeafMarkers({ punchlines, arPhotos, mapPhotos }).then((leaves) => {
      allLeaves = leaves;
      render();
    });
  });
  resizeObserver.observe(stageEl);

  // Après le 1er passage du ResizeObserver (qui remet la vue à zéro et
  // interromprait le zoom vers la photo s'il passait après).
  requestAnimationFrame(() => requestAnimationFrame(focusPhotoFromUrl));

  // ── Ajout de photo (même modal/logique que la vraie page Carte, cf.
  // mapPhotoUpload.js) : réutilisé tel quel, seul le rafraîchissement des
  // marqueurs après envoi diffère (recalcul des feuilles EqualEarth). ──
  createMapPhotoUploadFeature({
    geolocationService: createGeolocationService(),
    uploadMapPhoto,
    onUploadSuccess: async () => {
      mapPhotos = await fetchJson('/api/map-photos');
      allLeaves = await buildLeafMarkers({ punchlines, arPhotos, mapPhotos });
      render();
    },
  });
}

init().catch((error) => {
  console.error('[equalearth] erreur d’initialisation:', error);
});
